import type * as React from 'react';
import { cn } from '../../styles/lib/utils';
import { Badge } from '../Badge';
import { Text } from '../Text';

/** One layer in an export, split so the section it came from can be de-emphasised against its name. */
export type ExportLayerEntry = {
  id: string;
  /** TOC section the layer sits under, omitted for layers that have no parent. */
  parent?: string;
  label: string;
};

export type ExportLayerCardProps = React.ComponentProps<'div'> & {
  title: string;
  layers: ExportLayerEntry[];
  projectionLabel?: string;
  projectionTitle?: string;
};

/** Contents of the card shown when hovering an export's layer count. */
function ExportLayerCard({
  className,
  title,
  layers,
  projectionLabel,
  projectionTitle = 'Projection',
  ...props
}: ExportLayerCardProps) {
  return (
    <div
      data-slot="export-layer-card"
      className={cn(
        'bg-white border-grey-30 flex max-h-[50vh] w-max max-w-sm flex-col gap-2 overflow-y-auto rounded-lg border p-3 shadow-lg',
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between gap-3">
        <Text className="text-grey-90 font-medium">{title}</Text>
        <Badge kind="count" size="small" className="shrink-0">
          {layers.length.toLocaleString()}
        </Badge>
      </div>

      <ul className="flex flex-col gap-1">
        {layers.map((layer) => (
          <li key={layer.id} className="flex items-baseline gap-1">
            {layer.parent && (
              <>
                <Text className="text-grey-60 text-xs">{layer.parent}</Text>
                <Text aria-hidden className="text-grey-50 text-xs">
                  &rsaquo;
                </Text>
              </>
            )}
            <Text className="text-grey-90 text-xs">{layer.label}</Text>
          </li>
        ))}
      </ul>

      {projectionLabel && (
        <div className="flex flex-col">
          <Text className="text-grey-90 font-medium">{projectionTitle}</Text>
          <Text className="text-grey-80 text-xs">{projectionLabel}</Text>
        </div>
      )}
    </div>
  );
}

export { ExportLayerCard };
